import React, { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, AlertCircle, CheckCircle2, TriangleAlert, Download, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { formatEuro } from '@/lib/format';
import { IMPORT_TYPES, downloadTemplate } from '@/lib/estimating/templates';
import { parseTemplate, reconcile, sha256File } from '@/lib/estimating/parser';
import { uploadImportFile, createImport, insertLines, confirmImport, discardDraft } from '@/lib/estimating/service';
import { EST_IDS } from '@/constants/testIds/estimating';
import { ValidationList, PreviewTable } from './ImportPreview';

const FAILURE_TEXT = {
  no_expected_total: 'No confirmed Tender Summary and no expected total entered',
  boq_total_vs_expected: 'BOQ total differs from the expected ex-VAT total',
  tender_total_vs_expected: 'Tender Total differs from the expected total',
  components_plus_vat_vs_total: 'Components + VAT do not equal the Tender Total',
  cost_total_vs_expected: 'Cost total differs from the expected total',
};

export default function ImportDialog({ open, onOpenChange, project, importType, tenderSummary, onDone }) {
  const { user, organisationId } = useAuth();
  const t = IMPORT_TYPES[importType];
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualExpected, setManualExpected] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { if (!open) { setFile(null); setParsed(null); setManualExpected(''); setOverrideReason(''); setError(''); } }, [open]);

  const usesTender = importType === 'priced_boq' && tenderSummary?.tender_subtotal_ex_vat != null;
  const manualNum = manualExpected === '' ? null : Number(manualExpected);
  const expected = usesTender ? Number(tenderSummary.tender_subtotal_ex_vat) : (Number.isFinite(manualNum) ? manualNum : null);
  const rec = parsed ? reconcile(parsed, expected) : null;
  const hasErrors = parsed?.errors?.length > 0;
  const needsReason = rec && !rec.ok;
  const canConfirm = parsed && !hasErrors && (!needsReason || overrideReason.trim().length > 0);

  const handleFile = async (f) => {
    if (!f) return;
    setError(''); setParsed(null); setFile(f); setBusy(true);
    try { setParsed(await parseTemplate(f, importType)); }
    catch (e) { setError(e.message || 'Could not read the file'); setFile(null); }
    finally { setBusy(false); }
  };

  const handleConfirm = async (asDraft = false) => {
    if (asDraft ? !(parsed && !hasErrors) : !canConfirm) return;
    setSaving(true); setError('');
    let draft = null;
    try {
      const [sha256, up] = await Promise.all([sha256File(file), uploadImportFile({ file, organisationId, projectId: project.id, importType })]);
      draft = await createImport({
        project_id: project.id, organisation_id: organisationId, import_type: importType,
        file_name: up.filename, storage_bucket: 'estimate-files', storage_path: up.path,
        file_size_bytes: file.size, file_sha256: sha256, row_count: parsed.rows.length,
        manual_expected_total: usesTender ? null : expected, status: 'draft', uploaded_by: user?.id,
      });
      await insertLines(parsed, draft);
      if (asDraft) { toast.success(`${t.label} v${draft.version_number} saved as draft — enter rates, then confirm`); onDone?.(draft); onOpenChange(false); return; }
      const confirmed = await confirmImport({ importId: draft.id, overrideReason: needsReason ? overrideReason : null });
      toast.success(`${t.label} v${confirmed.version_number} confirmed`);
      onDone?.(confirmed);
      onOpenChange(false);
    } catch (e) {
      console.error('[estimating] import failed', e);
      if (draft?.id) { try { await discardDraft(draft.id); } catch (_) { /* best effort */ } }
      setError(e?.message || 'Import failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent data-testid={EST_IDS.importDialog} className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl tracking-tight">Import {t.label} — {project?.name}</DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-xs text-charcoal flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <span className="font-medium">Template rules:</span> row 1 = headings exactly as in the template · data from row 2 · blank rows ignored · one worksheet · no merged cells.
            Columns: {t.columns.map((c) => c.header + (c.required ? '*' : '')).join(', ')}.
          </div>
          <Button type="button" variant="outline" size="sm" data-testid={EST_IDS.templateButton(importType)} onClick={() => downloadTemplate(importType)}>
            <Download size={14} className="mr-1" /> Template
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="est-expected">
                {importType === 'priced_boq' ? 'Expected total ex VAT (€)' : importType === 'tender_summary' ? 'Expected tender total incl. VAT (€) — optional' : 'Expected cost total (€) — optional'}
              </Label>
              {usesTender ? (
                <div className="h-11 mt-1 flex items-center rounded-md border border-border bg-emerald-50 px-3 text-sm tabular-nums text-emerald-900" data-testid={EST_IDS.expectedInput}>
                  {formatEuro(expected)} <span className="ml-2 text-xs text-emerald-700">from confirmed Tender Summary v{tenderSummary.version_number}</span>
                </div>
              ) : (
                <Input id="est-expected" data-testid={EST_IDS.expectedInput} type="number" step="0.01" className="h-11 mt-1 tabular-nums"
                  value={manualExpected} onChange={(e) => setManualExpected(e.target.value)}
                  placeholder={importType === 'priced_boq' ? 'e.g. 1264317.18 (required until a Tender Summary is confirmed)' : 'e.g. 1435000.00'} />
              )}
            </div>
          </div>

        {!parsed && (
          <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            onClick={() => fileRef.current?.click()}
            className="rounded-lg border-2 border-dashed border-border bg-muted/30 px-6 py-10 text-center cursor-pointer hover:border-bronze transition-colors duration-150">
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid={EST_IDS.fileInput} onChange={(e) => handleFile(e.target.files?.[0])} />
            {busy ? (
              <div className="inline-flex items-center gap-2 text-sm"><Loader2 size={16} className="animate-spin text-bronze" /> Validating {file?.name}…</div>
            ) : (
              <>
                <div className="mx-auto h-12 w-12 rounded-full bg-warmgrey-soft text-warmgrey flex items-center justify-center"><Upload size={20} /></div>
                <div className="mt-3 font-medium text-charcoal">Drop the completed {t.label} template here, or click to browse</div>
                <div className="mt-1 text-xs text-muted-foreground">.xlsx only · validated in your browser before anything is saved</div>
              </>
            )}
          </div>
        )}

        {parsed && (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-3">
              <div className="min-w-0">
                <div className="font-medium text-charcoal truncate">{file.name}</div>
                <div className="text-xs text-muted-foreground">{parsed.rows.length} data rows · sheet "{parsed.sheet_name}"</div>
              </div>
              <button type="button" onClick={() => { setParsed(null); setFile(null); }} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-charcoal">
                <Trash2 size={12} /> Replace file
              </button>
            </div>

            <ValidationList errors={parsed.errors} warnings={parsed.warnings} />

            <div data-testid={EST_IDS.reconciliationCard} className="rounded-lg border border-border bg-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-semibold text-charcoal">Reconciliation <span className="text-xs text-muted-foreground font-normal">±€{rec.tolerance.toFixed(2)}</span></div>
                {rec.ok ? (
                  <Badge data-testid={EST_IDS.reconciliationMatch} className="bg-emerald-100 text-emerald-800 border-0 hover:bg-emerald-100"><CheckCircle2 size={12} className="mr-1" /> Reconciled</Badge>
                ) : (
                  <Badge data-testid={EST_IDS.reconciliationMismatch} className="bg-red-100 text-red-800 border-0 hover:bg-red-100"><TriangleAlert size={12} className="mr-1" /> Exception</Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <Stat label={importType === 'tender_summary' ? 'Tender total (file)' : 'File total'} value={formatEuro(rec.value)} />
                <Stat label="Expected" value={rec.expected != null ? formatEuro(rec.expected) : '—'} />
                <Stat label="Variance" value={rec.variance != null ? formatEuro(rec.variance) : '—'} tone={rec.ok ? 'text-emerald-700' : 'text-red-700'} />
              </div>
              {importType === 'tender_summary' && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Ex-VAT subtotal {formatEuro(parsed.tender_subtotal_ex_vat)} · VAT {formatEuro(parsed.tender_vat_amount)}{parsed.vat_rate != null ? ` (${parsed.vat_rate}%)` : ''} · Total {formatEuro(parsed.tender_total_inc_vat)}
                </div>
              )}
              {!rec.ok && (
                <ul className="mt-2 text-xs text-red-800 list-disc pl-5">{rec.failures.map((f) => <li key={f}>{FAILURE_TEXT[f] || f}</li>)}</ul>
              )}
              <div className="mt-2 text-[11px] text-muted-foreground">The database re-runs these checks from the saved lines before confirming.</div>
            </div>

            {needsReason && !hasErrors && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <Label htmlFor="est-override" className="text-red-900">Reason for override (required, saved to the audit log)</Label>
                <Textarea id="est-override" data-testid={EST_IDS.overrideReason} rows={3} className="mt-1 bg-white" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Explain why this import should be confirmed despite the exception" />
              </div>
            )}

            <PreviewTable parsed={parsed} />
          </div>
        )}

        {error && (
          <div data-testid={EST_IDS.importError} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /><span>{error}</span>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" data-testid={EST_IDS.cancelButton} disabled={saving} onClick={() => onOpenChange(false)}>Cancel</Button>
          {importType === 'priced_boq' && <Button type="button" variant="outline" data-testid="estimate-save-draft-button" disabled={!parsed || hasErrors || saving} onClick={() => handleConfirm(true)}>Save as draft (enter rates)</Button>}
          <Button type="button" data-testid={EST_IDS.confirmButton} disabled={!canConfirm || saving} onClick={() => handleConfirm(false)} className="bg-bronze hover:bg-bronze-hover text-white">
            {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : 'Confirm & save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone = 'text-charcoal' }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}
